import React, { useMemo } from 'react';
import { cn } from '../../lib/utils';

/**
 * Sparkline - SVG mini-chart for KPI cards.
 * Pure CSS / SVG (no chart library). 14 data points -> 14 line segments.
 *
 * Props:
 * - data: number[] (oldest -> newest)
 * - colour: tailwind text colour for the stroke (default: text-crm-blue)
 * - height: number (default 60)
 * - className: extra wrapper classes
 */
const Sparkline = ({ data = [], colour = 'text-crm-blue', height = 60, className = '' }) => {
  const { path, areaPath, viewBox } = useMemo(() => {
    if (!data.length) return { path: '', areaPath: '', viewBox: '0 0 100 60' };
    const W = 200;
    const H = height;
    const PAD_Y = 4;
    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = max - min || 1;
    const stepX = data.length > 1 ? W / (data.length - 1) : W;

    const points = data.map((v, i) => {
      const x = i * stepX;
      const norm = (v - min) / range; // 0..1
      const y = (H - PAD_Y) - norm * (H - PAD_Y * 2);
      return [x, y];
    });
    const pathStr = points.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(' ');
    const areaStr = `${pathStr} L ${W} ${H} L 0 ${H} Z`;
    return { path: pathStr, areaPath: areaStr, viewBox: `0 0 ${W} ${H}` };
  }, [data, height]);

  if (!data.length) return null;

  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="none"
      className={cn('block w-full', colour, className)}
      style={{ height: `${height}px` }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="sparkline-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#sparkline-fill)" />
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default Sparkline;
